/**
 * CustomFormsPage (§55.11) — tenant-defined input forms. Build a form (name + fields),
 * fill it to record a response, and review submissions. Use cases: pest scouting,
 * soil-test entry, crop quality grading, vet visit records.
 */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileSpreadsheet, Plus, X, Pencil, Trash2, ClipboardList } from 'lucide-react'
import { formsApi, type CustomForm, type FormField, type FormFieldType } from '../lib/api'

const FIELD_TYPES: FormFieldType[] = ['text', 'number', 'date', 'select', 'checkbox', 'textarea']
const card: React.CSSProperties = { background: 'var(--color-bg-card)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16 }
const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8, border: '1px solid var(--color-border)', background: 'var(--color-bg)', color: 'var(--color-text)' }
const btn = (primary?: boolean): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 8, border: primary ? 'none' : '1px solid var(--color-border)', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: primary ? 'var(--color-primary)' : 'none', color: primary ? '#fff' : 'var(--color-text-muted)' })
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'field'

export function CustomFormsPage() {
  const qc = useQueryClient()
  const [builder, setBuilder] = useState<CustomForm | 'new' | null>(null)
  const [filling, setFilling] = useState<CustomForm | null>(null)
  const [viewing, setViewing] = useState<CustomForm | null>(null)
  const { data: forms } = useQuery({ queryKey: ['forms'], queryFn: () => formsApi.list().then(r => r.data.forms) })
  const del = useMutation({ mutationFn: (id: string) => formsApi.remove(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['forms'] }) })

  return (
    <main id="main-content" style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-text)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileSpreadsheet size={20} /> Custom forms
        </h1>
        <button onClick={() => setBuilder('new')} style={{ ...btn(true), display: 'flex', alignItems: 'center', gap: 6 }}><Plus size={15} /> New form</button>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 18 }}>
        Build your own input forms — pest scouting, soil tests, quality grading, anything you record in the field.
      </p>

      {!forms?.length ? (
        <div style={{ ...card, textAlign: 'center', color: 'var(--color-text-faint)', fontSize: 13, padding: 40 }}>
          No forms yet. Click "New form" to design your first one.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {forms.map(f => (
            <div key={f.id} style={card}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)' }}>{f.name}</div>
              {f.description && <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>{f.description}</div>}
              <div style={{ fontSize: 11.5, color: 'var(--color-text-faint)', marginTop: 6 }}>{f.fields.length} field{f.fields.length !== 1 ? 's' : ''} · links to {f.resource_type}</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
                <button onClick={() => setFilling(f)} style={{ ...btn(true), padding: '6px 12px' }}>Fill out</button>
                <button onClick={() => setViewing(f)} style={{ ...btn(), padding: '6px 10px', display: 'flex', alignItems: 'center', gap: 4 }}><ClipboardList size={13} /> Responses</button>
                <button onClick={() => setBuilder(f)} title="Edit" style={{ ...btn(), padding: '6px 8px' }}><Pencil size={13} /></button>
                <button onClick={() => { if (confirm(`Delete "${f.name}"?`)) del.mutate(f.id) }} title="Delete" style={{ ...btn(), padding: '6px 8px', color: '#dc2626' }}><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {builder && <BuilderModal form={builder === 'new' ? null : builder} onClose={() => setBuilder(null)} />}
      {filling && <FillModal form={filling} onClose={() => setFilling(null)} />}
      {viewing && <ResponsesModal form={viewing} onClose={() => setViewing(null)} />}
    </main>
  )
}

// ── Builder ───────────────────────────────────────────────────────────────────
function BuilderModal({ form, onClose }: { form: CustomForm | null; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState(form?.name ?? '')
  const [description, setDescription] = useState(form?.description ?? '')
  const [resourceType, setResourceType] = useState(form?.resource_type ?? 'general')
  const [fields, setFields] = useState<FormField[]>(form?.fields ?? [{ key: 'note', label: 'Note', type: 'text', required: false }])

  const addField = () => setFields(f => [...f, { key: '', label: '', type: 'text', required: false }])
  const upd = (i: number, patch: Partial<FormField>) => setFields(f => f.map((x, j) => j === i ? { ...x, ...patch } : x))
  const rm = (i: number) => setFields(f => f.filter((_, j) => j !== i))

  const save = useMutation({
    mutationFn: () => {
      const cleaned = fields.filter(f => f.label.trim()).map(f => ({ ...f, key: f.key || slug(f.label), options: f.type === 'select' ? (f.options ?? []) : undefined }))
      const body = { name: name.trim(), description, resource_type: resourceType, fields: cleaned }
      return form ? formsApi.update(form.id, body) : formsApi.create(body)
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forms'] }); onClose() },
  })

  return (
    <Modal title={form ? 'Edit form' : 'New form'} onClose={onClose} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div><label style={lbl}>Form name</label><input style={inp} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Pest scouting" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <div><label style={lbl}>Description</label><input style={inp} value={description} onChange={e => setDescription(e.target.value)} placeholder="optional" /></div>
          <div><label style={lbl}>Links to</label>
            <select style={inp} value={resourceType} onChange={e => setResourceType(e.target.value)}>
              {['general', 'plant', 'block', 'zone', 'harvest', 'animal'].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label style={lbl}>Fields</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fields.map((f, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 70px 28px', gap: 6, alignItems: 'center' }}>
                <input style={inp} value={f.label} onChange={e => upd(i, { label: e.target.value, key: slug(e.target.value) })} placeholder="Field label" />
                <select style={inp} value={f.type} onChange={e => upd(i, { type: e.target.value as FormFieldType })}>
                  {FIELD_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <label style={{ fontSize: 11, color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input type="checkbox" checked={!!f.required} onChange={e => upd(i, { required: e.target.checked })} /> req
                </label>
                <button onClick={() => rm(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}><X size={15} /></button>
                {f.type === 'select' && (
                  <input style={{ ...inp, gridColumn: '1 / -1', fontSize: 12 }} value={(f.options ?? []).join(', ')} onChange={e => upd(i, { options: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} placeholder="Options, comma-separated" />
                )}
              </div>
            ))}
          </div>
          <button onClick={addField} style={{ ...btn(), marginTop: 8, padding: '6px 12px', display: 'flex', alignItems: 'center', gap: 4 }}><Plus size={13} /> Add field</button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={btn()}>Cancel</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !name.trim()} style={{ ...btn(true), opacity: !name.trim() ? 0.5 : 1 }}>{save.isPending ? 'Saving…' : 'Save form'}</button>
        </div>
      </div>
    </Modal>
  )
}

// ── Fill ──────────────────────────────────────────────────────────────────────
function FillModal({ form, onClose }: { form: CustomForm; onClose: () => void }) {
  const qc = useQueryClient()
  const [values, setValues] = useState<Record<string, unknown>>({})
  const set = (k: string, v: unknown) => setValues(p => ({ ...p, [k]: v }))
  const submit = useMutation({
    mutationFn: () => formsApi.submit(form.id, { resource_type: form.resource_type, values }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['form-responses', form.id] }); onClose() },
  })
  const missing = form.fields.some(f => f.required && (values[f.key] === undefined || values[f.key] === '' || values[f.key] === null))
  return (
    <Modal title={form.name} onClose={onClose}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {form.fields.map(f => (
          <div key={f.key}>
            <label style={lbl}>{f.label}{f.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
            {f.type === 'textarea' ? (
              <textarea style={{ ...inp, minHeight: 60 }} value={String(values[f.key] ?? '')} onChange={e => set(f.key, e.target.value)} />
            ) : f.type === 'select' ? (
              <select style={inp} value={String(values[f.key] ?? '')} onChange={e => set(f.key, e.target.value)}>
                <option value="">— choose —</option>
                {(f.options ?? []).map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            ) : f.type === 'checkbox' ? (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text)' }}>
                <input type="checkbox" checked={!!values[f.key]} onChange={e => set(f.key, e.target.checked)} /> Yes
              </label>
            ) : (
              <input style={inp} type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'} value={String(values[f.key] ?? '')} onChange={e => set(f.key, f.type === 'number' ? e.target.value : e.target.value)} />
            )}
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={onClose} style={btn()}>Cancel</button>
          <button onClick={() => submit.mutate()} disabled={submit.isPending || missing} style={{ ...btn(true), opacity: missing ? 0.5 : 1 }}>{submit.isPending ? 'Saving…' : 'Submit'}</button>
        </div>
      </div>
    </Modal>
  )
}

// ── Responses ───────────────────────────────────────────────────────────────
function ResponsesModal({ form, onClose }: { form: CustomForm; onClose: () => void }) {
  const qc = useQueryClient()
  const { data: responses } = useQuery({ queryKey: ['form-responses', form.id], queryFn: () => formsApi.responses(form.id).then(r => r.data.responses) })
  const del = useMutation({ mutationFn: (id: string) => formsApi.deleteResponse(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['form-responses', form.id] }) })
  return (
    <Modal title={`${form.name} — responses`} onClose={onClose} wide>
      {!responses?.length ? (
        <p style={{ fontSize: 13, color: 'var(--color-text-faint)' }}>No responses yet.</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--color-text-muted)' }}>
                <th style={{ padding: '6px 8px' }}>When</th>
                {form.fields.map(f => <th key={f.key} style={{ padding: '6px 8px' }}>{f.label}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {responses.map(r => (
                <tr key={r.id} style={{ borderTop: '1px solid var(--color-border)', color: 'var(--color-text)' }}>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'var(--color-text-muted)' }}>{new Date(r.submitted_at).toLocaleDateString()}</td>
                  {form.fields.map(f => <td key={f.key} style={{ padding: '6px 8px' }}>{fmtVal(r.values[f.key])}</td>)}
                  <td style={{ padding: '6px 8px' }}><button onClick={() => del.mutate(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}><X size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  )
}

function fmtVal(v: unknown): string {
  if (v === true) return '✓'
  if (v === false || v === undefined || v === null || v === '') return '—'
  return String(v)
}

function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ ...card, width: wide ? 620 : 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text)' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  )
}

export default CustomFormsPage
